import React, { useState, useMemo, useEffect, useCallback } from "react";
import CustomCard from "@/pages/leave/components/CustomCard";
import { Box, Grid, IconButton, TextField, Typography } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { format, eachDayOfInterval, parseISO } from 'date-fns';
import { useGetEmployeeMusterQuery, useGetEmployeeMusterListQuery, useGetMusterDynamicHeadersQuery } from "@/api/services/attendance/muster/Muster";
import { useGetPayrollLockDateQuery } from "@/api/services/attendance/calendar/Calendar";
import FormikMonthYearPicker from "@/components/forms/FormikMonthYearPicker";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import { StyledButton } from "@/components";
import { Form, Formik } from "formik";
import moment from "moment";
import CommonDataTable from "@/components/dataTableComponents/NewDatatablePagination";
import { DownloadIcon } from "@/icons";
import * as XLSX from 'xlsx';

const LeaveHistory = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [filterVisible, setFilterVisible] = useState(false);
  const [dataTableKey, setDataTableKey] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(moment().format("MM-YYYY"));
  const [empId, setEmpId] = useState(null);
  const [dynamicDateColumns, setDynamicDateColumns] = useState([]);
  const [loading, setLoading] = useState(false);

  const { data: payrollMonthData } = useGetPayrollLockDateQuery({ yearMonth: selectedMonth }, { skip: !selectedMonth });
  const payrollMonth = payrollMonthData?.data?.payrollMonth || {};

  const { data: dynamicHeadersData = [] } = useGetMusterDynamicHeadersQuery();
  const dynamicHeaders = dynamicHeadersData?.data || [];

  const { data: empListDetails = [] } = useGetEmployeeMusterListQuery();
  const empListData = empListDetails?.data || [];

  const employeeOptions = empListData.map((emp) => ({
    value: emp.empId,
    label: emp.empName,
  }));

  const { data: empMusterDetails = [], refetch: refetchEmpMuster, isLoading: empMusterFirstLoading, isUninitialized } =
    useGetEmployeeMusterQuery(
      {
        ...(empId ? { empId } : {}),
        yearMonth: selectedMonth,
        page: currentPage,
        size: pageSize,
        sortDirection: "DESC",
        search: searchText
      },
      { skip: !selectedMonth }
    );

  const rows = empMusterDetails?.data?.content || [];
  const totalRows = empMusterDetails?.data?.totalElements || 0;
  const pageCount = empMusterDetails?.data?.totalPages || Math.ceil(totalRows / pageSize);

  const refetchData = async () => {
    setLoading(true);
    try {
      await refetchEmpMuster();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isUninitialized) {
      refetchData();
    }
  }, [selectedMonth, empId, currentPage, pageSize, searchText, isUninitialized]);

  // useEffect(() => {
  //   if (!isUninitialized) {
  //     refetchEmpMuster();
  //   }
  // }, [selectedMonth, empId, currentPage, pageSize, searchText, refetchEmpMuster, isUninitialized]);

  useEffect(() => {
    if (payrollMonth?.startDate && payrollMonth?.endDate) {
      const startDate = parseISO(payrollMonth.startDate);
      const endDate = parseISO(payrollMonth.endDate);
      const columns = eachDayOfInterval({ start: startDate, end: endDate }).map((date) => ({
        field: format(date, "yyyy-MM-dd"),
        headerName: format(date, "dd iiiiii"),
        minWidth: 20,
      }));
      setDynamicDateColumns(columns);
    }
  }, [payrollMonth]);

  const flattenRowData = (row) => {
    const formattedDOJ = (doj) => {
      if (!doj) return "";
      const dateObj = new Date(typeof doj === "string" ? doj.replace(" IST", "") : doj);
      return isNaN(dateObj.getTime()) ? "Invalid Date" : format(dateObj, "yyyy-MM-dd");
    };

    // Extract dynamic headers if needed (optional if you're not using them)
    // const dynamicLeaveData = Object.entries(dynamicHeaders || {}).reduce((acc, [shortCode, label]) => {
    //   acc[label] = row[label] || 0;
    //   return acc;
    // }, {});

    // Extract from leaveCounts dynamically
    const leaveCounts = row.leaveCounts || {};
    const formattedLeaveCounts = Object.entries(leaveCounts).reduce((acc, [key, value]) => {
      acc[`${key}`] = value || 0;
      return acc;
    }, {});

    // Attendance mapping
    const attendanceMap = Object.fromEntries(
      row.attendanceData?.map(({ date, attendanceData }) => [date, attendanceData]) || []
    );

    return {
      employeeId: row.employeeId || "",
      employeeName: row.employeeName || "",
      designation: row.designation || "",
      doj: formattedDOJ(row.doj),
      location: row.location || "",
      month: row.month || "",
      totalPresentDays: row.totalPresentDays || 0,
      totalLOP: row.totalLOP || 0,
      weekOff: row.weekOff || 0,
      totalHolidays: row.totalHolidays || 0,
      totalDays: row.totalDays || 0,
      totalLeave: row.totalLeaveDays || 0,
      MaternityLeave: row["Maternity Leave"] || 0,
      ...formattedLeaveCounts,
      // ...dynamicLeaveData,          
      ...attendanceMap
    };
  };

  const flattenedRows = rows.map(flattenRowData);

  const staticColumns1 = [
    { field: "employeeId", headerName: "Employee ID", minWidth: 150 },
    { field: "employeeName", headerName: "Employee Name", minWidth: 150 },
    { field: "designation", headerName: "Designation", minWidth: 150 },
    { field: "doj", headerName: "DOJ", minWidth: 150 },
    { field: "location", headerName: "Location", minWidth: 150 },
    { field: "month", headerName: "Month", minWidth: 150 },
  ];

  const toPascalCase = (label) =>
    label.replace(/\s+/g, "").replace(/^\w/, (c) => c.toUpperCase());

  const dynamicLeaveColumns = Object.entries(dynamicHeaders).map(
    ([shortCode, label]) => ({
      field: label, // e.g., totalCasualLeave
      headerName: label,                    // e.g., Casual Leave
    })
  );


  const staticColumns2 = [
    { field: "totalPresentDays", headerName: "Total Present Days" },
    { field: "totalLOP", headerName: "Total LOP" },
    { field: "weekOff", headerName: "Week Off" },
    { field: "totalHolidays", headerName: "Holidays" },
    { field: "totalDays", headerName: "Total Days" },
    // { field: "totalSickLeave", headerName: "Sick Leave" },
    // { field: "totalCasualLeave", headerName: "Casual Leave" },
    // { field: "totalPrivilegeLeave", headerName: "Privilege Leave" },
    // { field: "totalProbationaryLeave", headerName: "Probationary Leave" },
    // { field: "totalLeave", headerName: "Total Leave" },
  ];

  const staticColumns3 = [
    { field: "MaternityLeave", headerName: "Maternity Leave" },
    { field: "Paternity Leave", headerName: "Paternity Leave" },
    { field: "Comp Off", headerName: "Comp Off" },
    { field: "RestrictedHoliday", headerName: "Restricted Holiday" },
    { field: "totalLeave", headerName: "Total Leave" },
  ];

  const columns = useMemo(() => [...staticColumns1, ...dynamicDateColumns, ...staticColumns2, ...dynamicLeaveColumns, ...staticColumns3], [dynamicDateColumns, dynamicLeaveColumns, staticColumns1, staticColumns2, staticColumns3]);
  const [visibleColumns, setVisibleColumns] = useState([]);

  useEffect(() => {
    const updated = columns.map((col) => ({ ...col, visible: true }));
    if (JSON.stringify(updated) !== JSON.stringify(visibleColumns)) {
      setVisibleColumns(updated);
    }
  }, [columns]);

  const handleToggleColumn = (field) => {
    setVisibleColumns((prev) =>
      prev.map((col) => (col.field === field ? { ...col, visible: !col.visible } : col))
    );
  };

  const renderedColumns = visibleColumns.filter((col) => col.visible);

  useEffect(() => {
    setDataTableKey((prev) => prev + 1);
  }, [visibleColumns]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
  }, []);

  const toggleFilter = () => setFilterVisible(!filterVisible);

  useEffect(() => {
    setCurrentPage(0); // Reset to page 0 when empId changes
  }, [empId]);

  // Reset pagination when month changes
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedMonth]);

  const downloadExcel = () => {
    // Create a new workbook and a worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(flattenedRows);

    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Muster Report");

    // Generate a binary string representation of the workbook
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create a Blob from the buffer
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });

    // Create a download link and trigger the download
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'MusterReport.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderContent = () => (
    <>

      <Grid container spacing={2} marginTop={2} marginBottom={2}>
        <Grid item xs={9}>
          <Typography variant="h6" sx={{ fontSize: '20px', color: '#067647' }}>Attendance Details</Typography>
        </Grid>
        <Grid item xs={3} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            sx={{
              color: "#fff",
              width: "40px",
              height: "40px",
              padding: "8.75px 9.21px",
              borderRadius: "4px",
              opacity: 1,
              backgroundColor: "#1677FF",
              margin: "0 10px",
              "&:hover": {
                backgroundColor: "#0D6EFD",
              },
            }}
            onClick={toggleFilter}
          >
            <FilterAltIcon />
          </IconButton>
        </Grid>
      </Grid>

      {filterVisible && (
        <Formik
          initialValues={{
            employee: "",
            startDate: selectedMonth ? moment(selectedMonth, "MM-YYYY").format("MMMM YYYY") : ""
          }}
          onSubmit={(values) => {
            setEmpId(values.employee || null);
            setSelectedMonth(values.startDate);
            setCurrentPage(0); // <-- reset page here
            setDataTableKey((prev) => prev + 1);
          }}
        >
          {({ setFieldValue, values, resetForm }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box display="flex" gap={2}>
                    <FormikSelectInput
                      name="employee"
                      label="Employee"
                      options={employeeOptions}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFieldValue("employee", value);
                        setEmpId(value || null);
                        setCurrentPage(0);
                      }}
                      search
                    />
                    <FormikMonthYearPicker
                      name="startDate"
                      label="Month Year"
                      pickerType="month"
                      value={values.startDate ? moment(values.startDate, "MM-YYYY").format("MMMM YYYY") : ""}
                      onChange={(formattedDate) => {
                        const formatted = formattedDate ? moment(formattedDate).format("MM-YYYY") : "";
                        setFieldValue("startDate", formatted);
                        setSelectedMonth(formatted);
                        setDataTableKey((prevKey) => prevKey + 1);
                      }}
                      maxDate={new Date()}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center">
                  <StyledButton
                    label="Reset"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      resetForm();
                      setSelectedMonth(moment().format("MM-YYYY"));
                      setEmpId(null);
                      setCurrentPage(0);
                    }}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      )}

      {/* download onsite attendance excel */}
      <Box sx={{mb: 2}}>
        <StyledButton
          icon={<DownloadIcon />}
          label="Download Excel"
          onClick={() => downloadExcel("download.xlsx")}
          size="medium"
          variant="outlined"
          type="button"
          disabled={
            !flattenedRows || flattenedRows.length === 0 ||loading || empMusterFirstLoading
          }
          sx={{
            mt: 1,
            borderRadius: "4px",
            color: "hsl(38,92%,50%)",
            background: "rgb(253,239,214)",
            border: "1px solid hsl(38,92%,50%)",
            "&:hover": {
              color: "hsl(38,92%,50%)",
              background: "rgb(253,239,214)",
              border: "1px solid hsl(38,92%,50%)",
            },
            ...( // if disabled, override styles
              (!flattenedRows || flattenedRows.length === 0||loading || empMusterFirstLoading) && {
                color: "#aaa",
                background: "#f0f0f0",
                border: "1px solid #ccc",
                cursor: "not-allowed",
              }
            ),
          }}
        />
      </Box>

      <CommonDataTable
        columns={renderedColumns}
        rows={flattenedRows}
        checkbox={false}
        search={false}
        loading={loading || empMusterFirstLoading}
        // onSearchChange={handleSearchChange}
        pageCount={pageCount}
        totalRecords={totalRows}
        onPageChange={handlePageChange}
        onPageSizeChange={handleRowsPerPageChange}
        currentPage={currentPage}
        currentPageSize={pageSize}
      />
    </>
  );

  return <CustomCard headers={["Attendance Muster"]} customTab={renderContent()} />;
};

export default LeaveHistory;
